/**
 * animateReel — turn an SVG builder into a short animated WebM reel.
 *
 * Uses the browser's MediaRecorder API + canvas.captureStream() — no
 * server-side ffmpeg or dependencies needed. Output is a WebM blob that
 * uploads directly to Instagram Reels, TikTok, X, YouTube Shorts, etc.
 *
 * Called like:
 *
 *   const blob = await animateReel({
 *     builder: (props) => buildStoryCardSVG({ ...baseProps, ...props }),
 *     width: 1080, height: 1920,
 *     duration_ms: 4000, fps: 30,
 *     animate: (t) => ({ user: countUpUser(baseUser, t) }),
 *   });
 *   downloadBlob(blob, "eviq-story-0047.webm");
 */

const DEFAULT_MIME_TYPES = [
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
    "video/mp4",
];

const pickMime = () => {
    for (const t of DEFAULT_MIME_TYPES) {
        if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(t)) return t;
    }
    return "video/webm";
};

const svgStringToImage = (svgString) =>
    new Promise((resolve, reject) => {
        const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = (e) => {
            URL.revokeObjectURL(url);
            reject(e);
        };
        img.src = url;
    });

export const animateReel = async ({
    builder,
    width,
    height,
    duration_ms = 4000,
    fps = 30,
    animate,
    scale = 1,
    onProgress,
}) => {
    if (typeof builder !== "function") throw new Error("animateReel: builder is required");
    if (typeof animate !== "function") throw new Error("animateReel: animate(t) is required");
    if (typeof MediaRecorder === "undefined") {
        throw new Error("Animated reels not supported in this browser");
    }

    const canvas = document.createElement("canvas");
    canvas.width = width * scale;
    canvas.height = height * scale;
    const ctx = canvas.getContext("2d");

    const stream = canvas.captureStream(fps);
    const mime = pickMime();
    const recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 6_000_000 });
    const chunks = [];
    recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunks.push(e.data);
    };
    const done = new Promise((resolve) => (recorder.onstop = resolve));
    recorder.start();

    const totalFrames = Math.max(1, Math.round((duration_ms / 1000) * fps));
    const frameDelay = 1000 / fps;

    for (let i = 0; i < totalFrames; i++) {
        const t = totalFrames === 1 ? 1 : i / (totalFrames - 1);
        const props = animate(t) || {};
        const svgString = builder(props);
        // eslint-disable-next-line no-await-in-loop
        const img = await svgStringToImage(svgString);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, frameDelay));
        if (onProgress) onProgress(i / (totalFrames - 1));
    }

    // Hold the last frame briefly so social players display it as a poster.
    await new Promise((r) => setTimeout(r, 400));
    recorder.stop();
    await done;

    return new Blob(chunks, { type: mime });
};

/**
 * Convenience helper — animate impact counters counting UP from 0 to their
 * final values over the reel duration. Returns an `animate(t)` function
 * suitable for `animateReel({...})`.
 */
export const countUpUser = (user) => {
    const easeOut = (x) => 1 - Math.pow(1 - x, 3);
    return (t) => {
        const e = easeOut(t);
        return {
            user: {
                ...user,
                trees_restored: Math.round(user.trees_restored * e),
                ocean_plastic_lbs: Math.round(user.ocean_plastic_lbs * e),
                batteries_protected: Math.round(user.batteries_protected * e),
                co2_saved_lbs: Math.round(user.co2_saved_lbs * e),
                battery_life_extended_years:
                    Math.round(user.battery_life_extended_years * e * 10) / 10,
            },
        };
    };
};

/** Same, but for community-level counters (Monthly Recap reel). */
export const countUpCommunity = (community) => {
    const easeOut = (x) => 1 - Math.pow(1 - x, 3);
    return (t) => {
        const e = easeOut(t);
        const next = {};
        for (const [k, v] of Object.entries(community || {})) {
            next[k] = typeof v === "number" ? v * e : v;
        }
        return { community: next };
    };
};
